import { forEach, uniqueId } from "lodash"

import { Channel } from "types/Channel"

import {
  ContextBridge,
  EmiterFunction,
  EventHandler,
  HandlerEvent,
  IpcRendererEvent,
  IpcRenderer,
  Shell,
  IpcMain,
} from "./WebElectronTypes"

const events: Record<Channel, EventHandler[]> = {}

function getEvent(ipcRenderer: IpcRenderer): IpcRendererEvent {
  return {
    senderId: 0,
    ports: [],
    sender: ipcRenderer,
  }
}

function clear() {
  forEach(events, (handlers, channel) => {
    handlers.map((handler) => window.removeEventListener(channel, handler as (e: Event) => void))
    delete events[channel]
  })
}

const ipcRenderer: IpcRenderer = {
  on(channel: Channel, emiter: EmiterFunction) {
    function handler(event: HandlerEvent) {
      const emiterResult = emiter(getEvent(ipcRenderer), ...(event?.detail?.args || []))
      if (event.detail) event.detail.result = emiterResult
    }
    if (!events[channel]) events[channel] = []
    events[channel].push(handler)
    window.addEventListener(channel, handler as (e: Event) => void)
    return ipcRenderer
  },
  once(channel: Channel, emiter: EmiterFunction) {
    const removeSelf = () => this.removeListener(channel, emiter)
    function handler(event: HandlerEvent) {
      const emiterResult = emiter(getEvent(ipcRenderer), ...(event?.detail?.args || []))
      if (event.detail) event.detail.result = emiterResult
      removeSelf()
    }
    if (!events[channel]) events[channel] = []
    window.addEventListener(channel, handler as (e: Event) => void)
  },
  invoke<R = any>(channel: Channel, ...args: any[]): Promise<R> {
    return new Promise((resolve, reject) => {
      const id = uniqueId("webIpcRenderer")
      this.once(`${channel}__IPC_INVOKE_SUCCESS_${id}`, (e, arg) => resolve(arg))
      this.once(`${channel}__IPC_INVOKE_ERROR_${id}`, (e, arg) => reject(arg))
      const event = new CustomEvent(channel, { detail: { args, id } })
      window.dispatchEvent(event)
    })
  },
  rawListeners(channel: Channel): Function[] {
    return (events[channel] || []) as Function[]
  },
  removeListener(channel: Channel, emiter: Function) {
    events[channel]?.forEach((handler, index) => {
      if (handler === emiter) {
        window.removeEventListener(channel, handler as (e: Event) => void)
        events[channel].splice(index, 1)
      }
    })
    return ipcRenderer
  },
  removeAllListeners(channel?: Channel): IpcRenderer {
    if (!channel) {
      clear()
      return ipcRenderer
    }
    const handlers = events[channel]
    handlers?.map((handler) => window.removeEventListener(channel, handler as (e: Event) => void))
    delete events[channel]
    return ipcRenderer
  },
  send(channel: Channel, ...args: any[]) {
    const evt = new CustomEvent(channel, { detail: { args } })
    window.dispatchEvent(evt)
  },
  sendSync<R = void>(channel: Channel, ...args: any[]): R {
    const event = new CustomEvent<{ args: any[]; result?: R }>(channel, {
      detail: { args },
    })
    window.dispatchEvent(event)
    return event.detail?.result as R
  },
}

const ipcMain: IpcMain = {
  ...ipcRenderer,
  handle<R = any>(channel: Channel, emiter: EmiterFunction<R>) {
    const sender = this.send
    function handler(event: HandlerEvent) {
      const { args = [], id } = event.detail || {}
      const emiterResult = emiter(getEvent(ipcMain), ...args)
      Promise.resolve(emiterResult)
        .then((res) => {
          sender(`${channel}__IPC_INVOKE_SUCCESS_${id}`, res)
        })
        .catch((error) => {
          sender(`${channel}__IPC_INVOKE_ERROR_${id}`, error)
        })
    }
    if (!events[channel]) events[channel] = []
    events[channel].push(handler)
    window.addEventListener(channel, handler as (e: Event) => void)
  },
}

const shell: Shell = {
  openExternal: (url: string, option: any): Promise<void> => {
    const { target, features } = option
    window.open(url, target || "_blank", features)?.focus()
    return Promise.resolve()
  },
}

const contextBridge: ContextBridge = {
  exposeInMainWorld: (name: "electron" | "process", obj: any) => {
    window[name] = obj
  },
}

export { contextBridge, ipcRenderer, ipcMain, shell }
